import axios, { AxiosResponse } from "axios";
import { getErrorMessage } from "../locales/translationUtils";

/**
 * Emulação de perfil e de cliente.
 *
 * Fica na API de autenticação, e não na de dados, porque emular é reescrever o claim
 * dentro do cookie — e é lá que o cookie é emitido. Como as duas APIs compartilham o
 * mesmo cookie, a de dados passa a obedecer sem nenhuma sincronização extra.
 */

interface ApiResponse<T> {
  data: T;
  messages: string[] | null;
}

export interface SituacaoEmulacao {
  emulando: boolean;
  perfilReal?: string | null;
  perfilEmulado?: string | null;
  clienteId?: number | null;
  clienteNome?: string | null;
}

export interface EscolhaDeEmulacao {
  perfil?: string | null;
  clienteId?: number | null;
  clienteNome?: string | null;
}

/** Perfis que podem ser emulados. Espelha PerfisDeAcesso.Emulaveis no back-end. */
export const PERFIS_EMULAVEIS = ["ROLE_ADMIN", "ROLE_USER", "ROLE_CLIENT"] as const;

/** Perfil que tem permissão de emular. */
export const PERFIL_QUE_PODE_EMULAR = "ROLE_MASTER";

const baseUrl: string | undefined = process.env.REACT_APP_BACK_END_AUTH_API_URL;
if (!baseUrl) throw new Error(getErrorMessage("missingEnvVariableError"));

/** Consulta a emulação vigente no cookie. */
export const buscaEmulacaoAtual = async (): Promise<SituacaoEmulacao | null> => {
  try {
    const response: AxiosResponse<ApiResponse<SituacaoEmulacao>> = await axios.get(
      `${baseUrl}Emulacao/atual`,
      { withCredentials: true }
    );

    return response.data.data;
  } catch {
    // Sem sessão válida não há emulação a mostrar; a tela de login cuida do resto.
    return null;
  }
};

/** Liga a emulação de perfil e/ou cliente. */
export const iniciaEmulacao = async (escolha: EscolhaDeEmulacao): Promise<SituacaoEmulacao> => {
  const response: AxiosResponse<ApiResponse<SituacaoEmulacao>> = await axios.post(
    `${baseUrl}Emulacao/iniciar`,
    {
      perfil: escolha.perfil ?? null,
      clienteId: escolha.clienteId ?? null,
      clienteNome: escolha.clienteNome ?? null,
    },
    { withCredentials: true }
  );

  return response.data.data;
};

/** Desliga a emulação e devolve o usuário ao próprio perfil. */
export const encerraEmulacao = async (): Promise<SituacaoEmulacao> => {
  const response: AxiosResponse<ApiResponse<SituacaoEmulacao>> = await axios.post(
    `${baseUrl}Emulacao/encerrar`,
    {},
    { withCredentials: true }
  );

  return response.data.data;
};
